import type {
  FollowUp,
  Measurement,
  Report,
  ReportUpdatePayload,
  FindingStatement,
} from '@/models/report.ts';
import {
  EMPTY_STATEMENTS,
  groupFindingStatements,
  statementSignature,
} from '@/features/report/Findings/FindingInput/utils/statements';
import { selectAIVariants } from '@/features/report/Measurements/rowModel';
import type { AIMeasurement } from '@/models/aiScan';
import { buildFindingPayloads } from './buildFindingPayloads';

/** The AI value each row displays, by canonical — the same variant the
 *  worksheet picks. */
export function aiDisplayValues(
  aiMeasurements: AIMeasurement[] | undefined
): Map<string, number | null> {
  const out = new Map<string, number | null>();
  selectAIVariants(aiMeasurements ?? []).forEach((roles, canonical) =>
    out.set(canonical, roles.display.value ?? null)
  );
  return out;
}

/** The number the row shows: manual, then AI, then SR. */
function displayedValue(
  m: Measurement,
  aiValue: number | null | undefined
): number | null {
  const isManual = m.valueSource === 'MANUAL' || !!m.annotation;
  if (isManual) return m.value;
  return aiValue ?? m.value;
}

/** Serializable fingerprint of one measurement row for delta detection.
 *  Unsaved is judged on what the reader sees: the displayed value, not the
 *  row's stored arm, and not its source label. A converge or a typed value
 *  that lands on the number already shown changes nothing visible. */
export function fingerprintMeasurement(
  m: Measurement,
  aiValue?: number | null
): string {
  return JSON.stringify({
    canonicalMeasurement: m.canonicalMeasurement,
    uuid: m.uuid ?? null,
    value: displayedValue(m, aiValue),
    unit: m.unit,
    annotation: m.annotation,
  });
}

export interface ReportEditBaseline {
  measurementByCanonical: Map<string, string>;
  /** findingClass → statementSignature; classes with no statements are absent. */
  findingByClass: Map<string, string>;
  conclusionNormalized: string;
  conclusionUuid?: string;
  flagsSorted: string;
  followUpSerialized: string | null;
  aiDeletedUuidsSorted: string;
  tagsSorted: string;
}

/** Kept as a boundary so splitting one paragraph into two still reads as an
 *  edit — without it `<p>AB</p>` and `<p>A</p><p>B</p>` would compare equal. */
const BLOCK_END = /<\/(?:p|li|ul|ol|div|h[1-6]|blockquote)>|<br\s*\/?>/gi;
const BOUNDARY = '␟';

/**
 * Reduce a conclusion to its visible text, block boundaries intact.
 *
 * The baseline holds the raw server HTML while the live value is whatever
 * TipTap re-serialized that same content into — once loaded,
 * `editor.getHTML()` owns the field. Comparing markup therefore compared two
 * *spellings* of identical content, and any study whose stored spelling
 * wasn't already canonical loaded permanently dirty (AP-683:
 * `<ul><li></li></ul>` vs ``). Comparing text retires the whole class —
 * empty lists, legacy plain text, `<b>` vs `<strong>`, entities, tags the
 * schema drops (including legacy `data-auto-added` bullet attributes).
 *
 * Trade-off accepted: a formatting-only edit no longer registers as unsaved.
 */
export function conclusionSignature(html: string): string {
  const el = document.createElement('div');
  el.innerHTML = html.replace(BLOCK_END, BOUNDARY);
  return (
    (el.textContent ?? '')
      // Collapse whitespace runs, then the boundaries they surrounded.
      .replace(new RegExp(`[^\\S${BOUNDARY}]+`, 'g'), ' ')
      .replace(new RegExp(`\\s*${BOUNDARY}\\s*`, 'g'), BOUNDARY)
      .replace(new RegExp(`${BOUNDARY}+`, 'g'), BOUNDARY)
      .replace(new RegExp(`^${BOUNDARY}+|${BOUNDARY}+$`, 'g'), '')
      .trim()
  );
}

function normalizeConclusion(
  conclusion: { uuid?: string; value: string } | null
): {
  normalized: string;
  uuid?: string;
} {
  return {
    normalized: conclusionSignature(conclusion?.value ?? ''),
    uuid: conclusion?.uuid,
  };
}

export function serializeFollowUp(f: FollowUp | null): string | null {
  if (!f) return null;
  return JSON.stringify({
    followUpClass: f.followUpClass,
    customValue: f.customValue,
  });
}

/** Editor state wins per class; otherwise the report's stored statements. */
export function buildEffectiveFindingMap(
  currentReport: Report,
  findingsState: Record<string, FindingStatement[]>
): Map<string, FindingStatement[]> {
  const map = new Map(
    Object.entries(groupFindingStatements(currentReport.findings))
  );
  Object.entries(findingsState).forEach(([fc, rows]) => {
    map.set(fc, rows);
  });
  return map;
}

export function captureReportEditBaseline(params: {
  currentReport: Report;
  findingsState: Record<string, FindingStatement[]>;
  conclusion: { uuid?: string; value: string } | null;
  selectedFlagClasses: string[];
  followUp: FollowUp | null;
}): ReportEditBaseline {
  const {
    currentReport,
    findingsState,
    conclusion,
    selectedFlagClasses,
    followUp,
  } = params;

  const measurementByCanonical = new Map<string, string>();
  const aiValues = aiDisplayValues(currentReport.aiMeasurements);
  (currentReport.measurements ?? [])
    .filter((m) => !!m.canonicalMeasurement)
    .forEach((m) => {
      measurementByCanonical.set(
        m.canonicalMeasurement,
        fingerprintMeasurement(m, aiValues.get(m.canonicalMeasurement))
      );
    });

  // Treat empty classes as absent so worksheet-seeded [] entries compare
  // equal to a server-shape capture that has no rows for the class.
  const findingByClass = new Map(
    Array.from(buildEffectiveFindingMap(currentReport, findingsState))
      .map(([fc, rows]) => [fc, statementSignature(rows)] as const)
      .filter(([, sig]) => sig !== EMPTY_STATEMENTS)
  );

  const { normalized, uuid } = normalizeConclusion(conclusion);

  const flagsSorted = [...selectedFlagClasses]
    .filter((fc) => !fc.includes('FOLLOW_UP'))
    .sort()
    .join('|');

  const aiDeleted = (currentReport.aiMeasurements ?? [])
    .filter((ai) => ai.isDeleted)
    .map((ai) => ai.uuid)
    .sort()
    .join('|');

  const tagsSorted = [...(currentReport.tags ?? [])].sort().join('|');

  return {
    measurementByCanonical,
    findingByClass,
    conclusionNormalized: normalized,
    conclusionUuid: uuid,
    flagsSorted,
    followUpSerialized: serializeFollowUp(followUp),
    aiDeletedUuidsSorted: aiDeleted,
    tagsSorted,
  };
}

/**
 * Capture a baseline directly from a server-shape Report (no local editor
 * state). Used to seed the baseline on initial reportData load and on every
 * mutation success — sidestepping the multi-effect seeding race that an
 * editor-state-based capture would hit.
 */
export function fingerprintFromReport(report: Report): ReportEditBaseline {
  return captureReportEditBaseline({
    currentReport: report,
    findingsState: {},
    conclusion: report.conclusion
      ? { uuid: report.conclusion.uuid, value: report.conclusion.value }
      : null,
    selectedFlagClasses: (report.flags ?? []).map((f) => f.flagClass),
    followUp: report.followUp ?? null,
  });
}

export type BaselineField =
  | 'conclusion'
  | 'conclusionUuid'
  | 'flags'
  | 'followUp'
  | 'aiDeleted'
  | 'tags'
  | 'measurements'
  | 'findings';

/** Which field made a report read as dirty. Named rather than reduced to a
 *  boolean so a false "unsaved changes" can be traced to its source — this is
 *  the third round of AP-683 and each one was a different field. */
export interface BaselineDivergence {
  field: BaselineField;
  /** canonicalMeasurement or findingClass, for the two map-valued fields. */
  key?: string;
  /** `undefined` means the key is absent on that side, not empty. */
  baseline: string | undefined;
  live: string | undefined;
}

/** First key whose value differs. Walking the union covers a size mismatch
 *  too — a differing size guarantees a key that one side lacks. */
function diffMaps(
  field: 'measurements' | 'findings',
  baseline: Map<string, string>,
  live: Map<string, string>
): BaselineDivergence | null {
  const keys = Array.from(new Set([...live.keys(), ...baseline.keys()]));
  const key = keys.find((k) => baseline.get(k) !== live.get(k));
  if (key === undefined) return null;
  return { field, key, baseline: baseline.get(key), live: live.get(key) };
}

/**
 * Compare a captured baseline against the current editor state and report the
 * first field that differs, or null when they match. Backs `unsavedChanges`
 * and, in dev, says *why* a report reads dirty.
 */
export function diffReportBaseline(
  baseline: ReportEditBaseline,
  params: {
    currentReport: Report;
    findingsState: Record<string, FindingStatement[]>;
    conclusion: { uuid?: string; value: string } | null;
    selectedFlagClasses: string[];
    followUp: FollowUp | null;
  }
): BaselineDivergence | null {
  const live = captureReportEditBaseline(params);

  const scalars: [BaselineField, string | undefined, string | undefined][] = [
    ['conclusion', baseline.conclusionNormalized, live.conclusionNormalized],
    ['conclusionUuid', baseline.conclusionUuid, live.conclusionUuid],
    ['flags', baseline.flagsSorted, live.flagsSorted],
    [
      'followUp',
      baseline.followUpSerialized ?? undefined,
      live.followUpSerialized ?? undefined,
    ],
    ['aiDeleted', baseline.aiDeletedUuidsSorted, live.aiDeletedUuidsSorted],
    ['tags', baseline.tagsSorted, live.tagsSorted],
  ];
  const scalar = scalars.find(([, b, l]) => b !== l);
  if (scalar) {
    const [field, b, l] = scalar;
    return { field, baseline: b, live: l };
  }

  const measurements = diffMaps(
    'measurements',
    baseline.measurementByCanonical,
    live.measurementByCanonical
  );
  if (measurements) return measurements;

  return diffMaps('findings', baseline.findingByClass, live.findingByClass);
}

/**
 * Cheap equality check between a captured baseline and the current editor
 * state. Used to derive `unsavedChanges` without an imperative dirty flag.
 * Returns true when current state matches baseline byte-for-byte.
 */
export function isReportEqualToBaseline(
  baseline: ReportEditBaseline,
  params: Parameters<typeof diffReportBaseline>[1]
): boolean {
  return diffReportBaseline(baseline, params) === null;
}

/** Build sparse PATCH body for addendum drafts — only fields that differ from baseline. */
export function pruneReportUpdateToDelta(params: {
  baseline: ReportEditBaseline;
  fullPayload: ReportUpdatePayload;
  effectiveFindingMap: Map<string, FindingStatement[]>;
  currentReport: Report;
}): ReportUpdatePayload {
  const { baseline, fullPayload, effectiveFindingMap, currentReport } = params;

  const out: ReportUpdatePayload = { uuid: fullPayload.uuid };

  if (fullPayload.status !== undefined) {
    out.status = fullPayload.status;
  }

  const measIn = fullPayload.measurements ?? [];
  const measKeysInPayload = new Set<string>(
    measIn
      .map((m) => m.canonicalMeasurement)
      .filter((v): v is string => Boolean(v))
  );

  const aiValues = aiDisplayValues(currentReport.aiMeasurements);
  const measDelta: Measurement[] = measIn.filter((m) => {
    if (!m.canonicalMeasurement) return true;
    const fp = fingerprintMeasurement(m, aiValues.get(m.canonicalMeasurement));
    return baseline.measurementByCanonical.get(m.canonicalMeasurement) !== fp;
  });

  Array.from(baseline.measurementByCanonical.keys())
    .filter((canonical) => !measKeysInPayload.has(canonical))
    .forEach((canonical) => {
      try {
        const prev = JSON.parse(
          baseline.measurementByCanonical.get(canonical)!
        ) as Pick<Measurement, 'uuid' | 'unit'> & {
          canonicalMeasurement: string;
        };
        measDelta.push({
          uuid: prev.uuid,
          canonicalMeasurement: prev.canonicalMeasurement ?? canonical,
          value: null,
          // Prefer the baseline unit when present; fall back only when missing/empty.
          unit: prev.unit || 'cm',
          annotation: null,
        });
      } catch {
        /* skip malformed baseline */
      }
    });

  if (measDelta.length) {
    out.measurements = measDelta;
  }

  // Every class the baseline knows is in the effective map, so walking the
  // map covers removals too.
  const changedClasses = Array.from(effectiveFindingMap)
    .filter(
      ([fc, rows]) =>
        (baseline.findingByClass.get(fc) ?? EMPTY_STATEMENTS) !==
        statementSignature(rows)
    )
    .map(([fc]) => fc);
  const findDelta = buildFindingPayloads(
    currentReport,
    Object.fromEntries(effectiveFindingMap),
    changedClasses
  );
  if (findDelta.length) {
    out.findings = findDelta;
  }

  const conclusionPayload = fullPayload.conclusion;
  // Same normalization the baseline was captured with, or the delta would
  // carry an untouched conclusion on every save.
  const concTrimmed = conclusionSignature(conclusionPayload?.value ?? '');
  if (
    concTrimmed !== baseline.conclusionNormalized ||
    (conclusionPayload?.uuid ?? undefined) !== baseline.conclusionUuid
  ) {
    out.conclusion = conclusionPayload ?? null;
  }

  const flagsKey = (fullPayload.flags ?? [])
    .map((f) => f.flagClass)
    .sort()
    .join('|');
  if (flagsKey !== baseline.flagsSorted && fullPayload.flags) {
    out.flags = fullPayload.flags;
  }

  const fuSer = serializeFollowUp(fullPayload.followUp ?? null);
  if (fuSer !== baseline.followUpSerialized) {
    out.followUp = fullPayload.followUp ?? null;
  }

  const aiIn = fullPayload.aiMeasurements ?? [];
  if (aiIn.length) {
    const baselineDeleted = new Set(
      baseline.aiDeletedUuidsSorted.split('|').filter(Boolean)
    );
    const aiDelta = aiIn.filter(
      (row) => row.isDeleted && !baselineDeleted.has(row.uuid)
    );
    if (aiDelta.length) {
      out.aiMeasurements = aiDelta;
    }
  }

  if (fullPayload.tags !== undefined) {
    const tagsKey = [...fullPayload.tags].sort().join('|');
    if (tagsKey !== baseline.tagsSorted) {
      out.tags = fullPayload.tags;
    }
  }

  return out;
}
